import React, { useCallback, useEffect, useState } from 'react'
import Button from '../common/Button'
import Icon from '../common/Icon'
import Dropdown from '../common/Dropdown'
import ConfirmDialog from '../common/ConfirmDialog'
import Spinner from '../common/Spinner'
import {
  Company,
  COMPANIES,
  CustomFields,
  ModelExample,
  ImplementedModel,
  SemanticModelRef,
  deleteModel,
  emptyCustomFields,
  getAvailableModels,
  getImplementedModels,
  saveModel,
  updateModel,
} from '../../api/settingsService'

type View = 'list' | 'editor'

// Pull the FastAPI error detail out of an axios error, falling back to a default.
const serverMessage = (e: unknown, fallback: string): string => {
  const detail = (e as { response?: { data?: { detail?: string } } })?.response
    ?.data?.detail
  return typeof detail === 'string' && detail ? detail : fallback
}

// Unix seconds -> "5 Jun 2026"
const formatDate = (ts: number): string =>
  new Date(ts * 1000).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })

// Editor for a string[] field: one input per item, add/remove rows.
const StringListField: React.FC<{
  label: string
  hint: string
  placeholder: string
  items: string[]
  onChange: (items: string[]) => void
}> = ({ label, hint, placeholder, items, onChange }) => (
  <div className="semantic-models__field">
    <span className="semantic-models__label">{label}</span>
    <span className="semantic-models__hint">{hint}</span>
    {items.map((item, i) => (
      <div key={i} className="semantic-models__list-row">
        <input
          className="semantic-models__input"
          value={item}
          placeholder={placeholder}
          onChange={e =>
            onChange(items.map((v, j) => (j === i ? e.target.value : v)))
          }
        />
        <button
          type="button"
          className="semantic-models__row-remove"
          onClick={() => onChange(items.filter((_, j) => j !== i))}
          aria-label="Remove"
        >
          ×
        </button>
      </div>
    ))}
    <button
      type="button"
      className="semantic-models__add-row"
      onClick={() => onChange([...items, ''])}
    >
      + Add
    </button>
  </div>
)

// Editor for ModelExample[]: a question + DAX per example.
const ExamplesField: React.FC<{
  examples: ModelExample[]
  onChange: (examples: ModelExample[]) => void
}> = ({ examples, onChange }) => (
  <div className="semantic-models__field">
    <span className="semantic-models__label">Examples</span>
    <span className="semantic-models__hint">
      Worked questions and the DAX that answers them — the strongest guidance
    </span>
    {examples.map((ex, i) => (
      <div key={i} className="semantic-models__example">
        <input
          className="semantic-models__input"
          placeholder="Question, e.g. Top 10 bakeries last week"
          value={ex.question}
          onChange={e =>
            onChange(
              examples.map((v, j) =>
                j === i ? { ...v, question: e.target.value } : v
              )
            )
          }
        />
        <textarea
          className="semantic-models__textarea semantic-models__code"
          placeholder="DAX"
          rows={3}
          value={ex.dax}
          onChange={e =>
            onChange(
              examples.map((v, j) =>
                j === i ? { ...v, dax: e.target.value } : v
              )
            )
          }
        />
        <button
          type="button"
          className="semantic-models__row-remove semantic-models__example-remove"
          onClick={() => onChange(examples.filter((_, j) => j !== i))}
        >
          Remove example
        </button>
      </div>
    ))}
    <button
      type="button"
      className="semantic-models__add-row"
      onClick={() => onChange([...examples, { question: '', dax: '' }])}
    >
      + Add example
    </button>
  </div>
)

const InlineLoading: React.FC<{ text?: string }> = ({ text = 'Loading…' }) => (
  <div className="semantic-models__loading">
    <Spinner text={text} />
  </div>
)

const SemanticModelsPanel: React.FC = () => {
  const [view, setView] = useState<View>('list')
  const [implemented, setImplemented] = useState<ImplementedModel[]>([])
  const [available, setAvailable] = useState<SemanticModelRef[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [availableError, setAvailableError] = useState<string | null>(null)

  // Editor state
  const [editingExisting, setEditingExisting] = useState(false)
  const [selected, setSelected] = useState<SemanticModelRef | null>(null)
  const [company, setCompany] = useState<Company>('gails')
  const [fields, setFields] = useState<CustomFields>(emptyCustomFields())
  const [saving, setSaving] = useState(false)

  // Delete confirmation
  const [pendingDelete, setPendingDelete] = useState<ImplementedModel | null>(null)

  // dataset_ids of models currently being refreshed from Fabric. Refreshes of
  // different models are independent, so several can run at once; only the
  // refreshing model's own actions are locked.
  const [refreshingIds, setRefreshingIds] = useState<Set<string>>(new Set())

  const loadLists = useCallback(async () => {
    setLoading(true)
    setError(null)
    setAvailableError(null)
    // Saved models come from Cosmos and should load even if Fabric is unavailable.
    try {
      setImplemented(await getImplementedModels())
    } catch (e) {
      setError(serverMessage(e, 'Could not load saved models.'))
    }
    // Available models require a Fabric token; isolate its failure to the Add flow.
    try {
      setAvailable(await getAvailableModels())
    } catch (e) {
      setAvailableError(
        serverMessage(
          e,
          'Could not load available Fabric models. Check Fabric access.'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadLists()
  }, [loadLists])

  const openAdd = () => {
    setEditingExisting(false)
    setSelected(null)
    setFields(emptyCustomFields())
    setError(null)
    setView('editor')
  }

  const openEdit = (model: ImplementedModel) => {
    setEditingExisting(true)
    setSelected({
      dataset_id: model.dataset_id,
      workspace_id: model.workspace_id,
      name: model.name,
      is_implemented: true,
    })
    setCompany(model.company)
    setFields(model.custom_fields)
    setError(null)
    setView('editor')
  }

  const onSelectModel = (datasetId: string) => {
    setSelected(available.find(m => m.dataset_id === datasetId) || null)
    setCompany('gails')
    setFields(emptyCustomFields())
  }

  const onSave = async () => {
    if (!selected) return
    setSaving(true)
    setError(null)
    try {
      const req = {
        dataset_id: selected.dataset_id,
        workspace_id: selected.workspace_id,
        name: selected.name,
        company,
        custom_fields: fields,
      }
      if (editingExisting) {
        await updateModel(selected.dataset_id, req)
      } else {
        await saveModel(req)
      }
      await loadLists()
      setView('list')
    } catch (e) {
      setError(serverMessage(e, 'Could not save the model.'))
    } finally {
      setSaving(false)
    }
  }

  const doDelete = async () => {
    if (!pendingDelete) return
    const model = pendingDelete
    setPendingDelete(null)
    try {
      await deleteModel(model.dataset_id)
      await loadLists()
    } catch (e) {
      setError(serverMessage(e, 'Could not delete the model.'))
    }
  }

  // Re-save with the stored values: the backend re-fetches the Fabric
  // definition and rebuilds the document, keeping the custom fields as-is.
  const doRefresh = async (model: ImplementedModel) => {
    setRefreshingIds(prev => new Set(prev).add(model.dataset_id))
    setError(null)
    try {
      await updateModel(model.dataset_id, {
        dataset_id: model.dataset_id,
        workspace_id: model.workspace_id,
        name: model.name,
        company: model.company,
        custom_fields: model.custom_fields,
      })
      await loadLists()
    } catch (e) {
      setError(serverMessage(e, `Could not refresh "${model.name}" from Fabric.`))
    } finally {
      setRefreshingIds(prev => {
        const next = new Set(prev)
        next.delete(model.dataset_id)
        return next
      })
    }
  }

  const addableOptions = available
    .filter(m => !m.is_implemented)
    .map(m => ({ value: m.dataset_id, label: m.name }))

  if (loading) {
    return <InlineLoading />
  }

  if (view === 'list') {
    return (
      <div className="semantic-models">
        <div className="semantic-models__header">
          <div>
            <h3 className="semantic-models__title">Semantic Models</h3>
            <p className="semantic-models__subtitle">
              Models BreadGPT can use to answer reporting questions.
            </p>
          </div>
          <Button variant="primary" onClick={openAdd} disabled={!!availableError}>
            <Icon name="new-chat" size={16} /> Add model
          </Button>
        </div>

        {error && <div className="semantic-models__error">{error}</div>}
        {availableError && (
          <div className="semantic-models__warning">{availableError}</div>
        )}

        {implemented.length === 0 ? (
          <div className="semantic-models__empty">
            No models added yet. Click “Add model” to get started.
          </div>
        ) : (
          <ul className="semantic-models__list">
            {implemented.map(model => {
              const refreshing = refreshingIds.has(model.dataset_id)
              return (
              <li key={model.id} className="semantic-models__item">
                <div className="semantic-models__item-info">
                  <span className="semantic-models__item-name">
                    {model.name}
                    <span className="semantic-models__company-badge">
                      {COMPANIES.find(c => c.value === model.company)?.label ||
                        model.company}
                    </span>
                  </span>
                  {model.created_by && (
                    <span className="semantic-models__item-meta">
                      Created by {model.created_by}
                      {model.created_at ? ` · ${formatDate(model.created_at)}` : ''}
                    </span>
                  )}
                  {model.updated_by && model.updated_at && (
                    <span className="semantic-models__item-meta">
                      Updated by {model.updated_by} · {formatDate(model.updated_at)}
                    </span>
                  )}
                </div>
                <div className="semantic-models__item-actions">
                  <button
                    className={`semantic-models__icon-btn${
                      refreshing ? ' semantic-models__icon-btn--spinning' : ''
                    }`}
                    onClick={() => doRefresh(model)}
                    disabled={refreshing || !!availableError}
                    aria-label="Refresh from Fabric"
                    title="Re-fetch the model definition from Fabric and rebuild the document"
                  >
                    <Icon name="refresh" size={16} />
                  </button>
                  <button
                    className="semantic-models__icon-btn"
                    onClick={() => openEdit(model)}
                    disabled={refreshing}
                    aria-label="Edit"
                  >
                    <Icon name="edit" size={16} />
                  </button>
                  <button
                    className="semantic-models__icon-btn"
                    onClick={() => setPendingDelete(model)}
                    disabled={refreshing}
                    aria-label="Delete"
                  >
                    <Icon name="trash" size={16} />
                  </button>
                </div>
              </li>
              )
            })}
          </ul>
        )}

        <ConfirmDialog
          isOpen={!!pendingDelete}
          title="Remove model"
          message={`Remove "${pendingDelete?.name}" from BreadGPT? This deletes its saved document.`}
          confirmLabel="Remove"
          onConfirm={doDelete}
          onCancel={() => setPendingDelete(null)}
        />
      </div>
    )
  }

  // editor view
  return (
    <div className="semantic-models">
      <div className="semantic-models__subhead">
        <button className="semantic-models__back" onClick={() => setView('list')}>
          <Icon name="chevron-left" size={16} /> Back
        </button>
        <h3 className="semantic-models__title">
          {editingExisting ? `Edit ${selected?.name}` : 'Add a model'}
        </h3>
      </div>

      {error && <div className="semantic-models__error">{error}</div>}

      {!editingExisting && (
        <label className="semantic-models__field">
          <span className="semantic-models__label">Semantic model</span>
          <Dropdown
            options={addableOptions}
            value={selected?.dataset_id || ''}
            onChange={onSelectModel}
            placeholder="Select a model…"
          />
        </label>
      )}

      {selected && (
        <div className="semantic-models__form">
          <label className="semantic-models__field">
            <span className="semantic-models__label">Company</span>
            <span className="semantic-models__hint">
              Which business this model belongs to — controls who can query it
            </span>
            <Dropdown
              options={COMPANIES}
              value={company}
              onChange={v => setCompany(v as Company)}
            />
          </label>
          <label className="semantic-models__field">
            <span className="semantic-models__label">Description</span>
            <span className="semantic-models__hint">
              What this model is — data, grain, coverage
            </span>
            <textarea
              className="semantic-models__textarea"
              rows={3}
              value={fields.description}
              onChange={e =>
                setFields({ ...fields, description: e.target.value })
              }
            />
          </label>
          <StringListField
            label="Aliases"
            hint="Other names users say, e.g. TBF, wholesale"
            placeholder="alias"
            items={fields.aliases}
            onChange={aliases => setFields({ ...fields, aliases })}
          />
          <StringListField
            label="When to use"
            hint="Situations where this model is the right one"
            placeholder="use this when…"
            items={fields.use_when}
            onChange={use_when => setFields({ ...fields, use_when })}
          />
          <StringListField
            label="When NOT to use"
            hint="When a different model fits better"
            placeholder="do not use when… (e.g. → use GailsWOW)"
            items={fields.do_not_use_when}
            onChange={do_not_use_when =>
              setFields({ ...fields, do_not_use_when })
            }
          />
          <StringListField
            label="How to query"
            hint="One instruction per line: measures, filter columns, date rules, gotchas"
            placeholder="instruction"
            items={fields.query_instructions}
            onChange={query_instructions =>
              setFields({ ...fields, query_instructions })
            }
          />
          <ExamplesField
            examples={fields.examples}
            onChange={examples => setFields({ ...fields, examples })}
          />
          <div className="semantic-models__actions">
            <Button variant="secondary" onClick={() => setView('list')}>
              Cancel
            </Button>
            <Button variant="primary" onClick={onSave} isLoading={saving}>
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default SemanticModelsPanel
